
import { useState, useEffect } from 'react';
import { 
  AreaChart, 
  Area, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  BarChart,
  Bar,
  Legend,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

type DateRange = {
  from: Date;
  to: Date;
};

// Dados de exemplo para emoções - com datas mais variadas
const allEmotionData = [
  { date: new Date('2025-05-14'), name: 'Segunda', ansiedade: 4, estresse: 7, calma: 3, felicidade: 5 },
  { date: new Date('2025-05-15'), name: 'Terça', ansiedade: 6, estresse: 5, calma: 4, felicidade: 6 },
  { date: new Date('2025-05-16'), name: 'Quarta', ansiedade: 8, estresse: 8, calma: 2, felicidade: 3 },
  { date: new Date('2025-05-17'), name: 'Quinta', ansiedade: 5, estresse: 6, calma: 5, felicidade: 4 },
  { date: new Date('2025-05-18'), name: 'Sexta', ansiedade: 3, estresse: 4, calma: 7, felicidade: 7 },
  { date: new Date('2025-05-19'), name: 'Sábado', ansiedade: 2, estresse: 3, calma: 8, felicidade: 8 },
  { date: new Date('2025-05-20'), name: 'Domingo', ansiedade: 1, estresse: 2, calma: 9, felicidade: 9 },
  { date: new Date('2025-05-21'), name: 'Segunda', ansiedade: 3, estresse: 5, calma: 6, felicidade: 7 },
  { date: new Date('2025-05-07'), name: 'Terça', ansiedade: 5, estresse: 7, calma: 4, felicidade: 3 },
  { date: new Date('2025-05-08'), name: 'Quarta', ansiedade: 7, estresse: 6, calma: 3, felicidade: 4 },
  { date: new Date('2025-05-09'), name: 'Quinta', ansiedade: 4, estresse: 5, calma: 6, felicidade: 5 },
  { date: new Date('2025-05-10'), name: 'Sexta', ansiedade: 2, estresse: 3, calma: 8, felicidade: 7 },
  { date: new Date('2025-05-11'), name: 'Sábado', ansiedade: 1, estresse: 2, calma: 9, felicidade: 8 },
  { date: new Date('2025-05-12'), name: 'Domingo', ansiedade: 2, estresse: 1, calma: 8, felicidade: 9 },
];

const EMOTION_COLORS = {
  ansiedade: '#f87171', // vermelho
  estresse: '#fb923c', // laranja
  calma: '#60a5fa', // azul
  felicidade: '#34d399', // verde
};

const PIE_COLORS = ['#60a5fa', '#f87171', '#34d399', '#fb923c'];

interface EmotionChartProps {
  dateRange?: DateRange;
}

const EmotionChart = ({ dateRange = { from: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000), to: new Date() } }: EmotionChartProps) => {
  const [activeTab, setActiveTab] = useState('linha');
  const [filteredData, setFilteredData] = useState(allEmotionData);
  const [emotionDistribution, setEmotionDistribution] = useState([
    { name: 'Calma', value: 38 },
    { name: 'Ansiedade', value: 29 },
    { name: 'Felicidade', value: 42 },
    { name: 'Estresse', value: 35 },
  ]);

  // Filter data based on date range
  useEffect(() => {
    const filtered = allEmotionData.filter(entry => 
      entry.date >= dateRange.from && entry.date <= dateRange.to
    );
    
    setFilteredData(filtered);
    
    // Update emotion distribution based on filtered data
    if (filtered.length > 0) {
      const calmaTotal = filtered.reduce((sum, entry) => sum + entry.calma, 0);
      const ansiedadeTotal = filtered.reduce((sum, entry) => sum + entry.ansiedade, 0);
      const felicidadeTotal = filtered.reduce((sum, entry) => sum + entry.felicidade, 0);
      const estresseTotal = filtered.reduce((sum, entry) => sum + entry.estresse, 0);
      
      setEmotionDistribution([
        { name: 'Calma', value: calmaTotal },
        { name: 'Ansiedade', value: ansiedadeTotal },
        { name: 'Felicidade', value: felicidadeTotal },
        { name: 'Estresse', value: estresseTotal },
      ]);
    }
  }, [dateRange]);

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle>Rastreamento de Emoções</CardTitle>
        <CardDescription>Análise das suas emoções ao longo do tempo</CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="linha" className="w-full" onValueChange={setActiveTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="linha">Linha</TabsTrigger>
            <TabsTrigger value="barras">Barras</TabsTrigger>
            <TabsTrigger value="pizza">Distribuição</TabsTrigger>
          </TabsList>
          
          <TabsContent value="linha" className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={filteredData}
                margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis label={{ value: 'Intensidade (1-10)', angle: -90, position: 'insideLeft' }} />
                <Tooltip />
                <Legend />
                <Area 
                  type="monotone" 
                  dataKey="calma" 
                  stroke={EMOTION_COLORS.calma} 
                  fill={EMOTION_COLORS.calma} 
                  fillOpacity={0.3} 
                />
                <Area 
                  type="monotone" 
                  dataKey="ansiedade" 
                  stroke={EMOTION_COLORS.ansiedade} 
                  fill={EMOTION_COLORS.ansiedade} 
                  fillOpacity={0.3} 
                />
                <Area 
                  type="monotone" 
                  dataKey="felicidade" 
                  stroke={EMOTION_COLORS.felicidade} 
                  fill={EMOTION_COLORS.felicidade} 
                  fillOpacity={0.3} 
                />
                <Area 
                  type="monotone" 
                  dataKey="estresse" 
                  stroke={EMOTION_COLORS.estresse} 
                  fill={EMOTION_COLORS.estresse} 
                  fillOpacity={0.3} 
                />
              </AreaChart>
            </ResponsiveContainer>
          </TabsContent>
          
          <TabsContent value="barras" className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={filteredData}
                margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis label={{ value: 'Intensidade (1-10)', angle: -90, position: 'insideLeft' }} />
                <Tooltip />
                <Legend />
                <Bar dataKey="calma" name="Calma" fill={EMOTION_COLORS.calma} />
                <Bar dataKey="ansiedade" name="Ansiedade" fill={EMOTION_COLORS.ansiedade} />
                <Bar dataKey="felicidade" name="Felicidade" fill={EMOTION_COLORS.felicidade} />
                <Bar dataKey="estresse" name="Estresse" fill={EMOTION_COLORS.estresse} />
              </BarChart>
            </ResponsiveContainer>
          </TabsContent>
          
          <TabsContent value="pizza" className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={emotionDistribution}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                >
                  {emotionDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default EmotionChart;
